// src/services/loyaltyService.ts
import apiClient from '../api/apiClient';

export interface StylistBreakdown {
  stylist?: { _id: string; name?: string } | null;
  availablePoints: number;
  loyaltyEnabled?: boolean;
  pointsPerDollar?: number;
}

export interface LoyaltyData {
  loyaltyPoints: number;
  loyaltyTier?: string;
  transactions: any[];
  breakdown?: StylistBreakdown[];
}

// when you redeem, the backend should return the discount plus the updated loyaltyData
export interface RedeemResponse {
  discount: number;
  loyaltyPoints: number;
  redemptionId: string;
  transaction: any;
  pointsRedeemed?: number;
  stylistId?: string;
  idempotentReplay?: boolean;
}

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `lr_${crypto.randomUUID()}`;
  }
  return `lr_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

const loyaltyService = {
  getLoyalty: async (): Promise<LoyaltyData> => {
    const res = await apiClient.get('/loyalty');
    return res.data.data as LoyaltyData;
  },

  redeemPoints: async (
    pointsToRedeem: number,
    servicePrice: number,
    stylistId: string,
    options?: { serviceName?: string; idempotencyKey?: string }
  ): Promise<RedeemResponse> => {
    const idempotencyKey = options?.idempotencyKey || createIdempotencyKey();
    const res = await apiClient.post('/loyalty/redeem', {
      pointsToRedeem,
      servicePrice,
      stylistId,
      serviceName: options?.serviceName,
      idempotencyKey,
    });
    const data = res.data.data;
    return {
      discount: data.discount,
      loyaltyPoints: data.loyaltyPoints,
      redemptionId: data.redemptionId || data.transaction?._id,
      transaction: data.transaction,
      idempotentReplay: data.idempotentReplay,
    };
  },

  releaseLoyaltyRedemption: async (
    redemptionId: string,
    reason?: string,
    options?: { idempotencyKey?: string }
  ): Promise<void> => {
    if (!redemptionId) {
      throw new Error('loyaltyRedemptionId is required to release a redemption');
    }
    await apiClient.post('/loyalty/redeem/release', {
      loyaltyRedemptionId: redemptionId,
      ...(options?.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
      reason: reason || 'Client cancelled before booking completed',
    });
  },
};

export default loyaltyService;
